import { ReactNode } from 'react';

import { UserRole } from 'app-types/core';
import {
  Analytics,
  Dashboard,
  DeskIcon,
  ExchangeV2,
  HelpFilled,
  InvoiceChecking,
  Notifications,
  Orders,
  Tracking,
} from 'icons';

import {
  EnumSettingRoutes,
  GeneralRoutes,
  InvoiceCheckingTabRoutes,
  OrdersTabRoutes,
  ReturnsTabRoutes,
} from '../constants';

export enum NavbarMenuGroup {
  shipping = 'shipping',
  postShipping = 'postShipping',
  secondaryMenu = 'secondaryMenu',
  mobile = 'mobile',
}

export interface NavbarMenuItemData {
  id: MenuPages;
  to: string;
  icon: ReactNode;
  roles: UserRole[];
  // First-level app-module key (see src/lib/roles.js on the backend). When set,
  // the tab is hidden unless the user's effective access includes this module.
  // Omit for non-gated items (dashboard, support, history).
  moduleKey?: string;
  langKey: string;
  groups: NavbarMenuGroup[];
  divider?: boolean;
  warning?: boolean;
}

export const v2Path = '/v2';

export enum MenuPages {
  orders = 'orders',
  picking = 'picking',
  shipping = 'shipping',
  tracking = 'tracking',
  returns = 'returns',
  history = 'history',
  courierSupport = 'courierSupport',
  emailsSent = 'emailsSent',
  desk = 'desk',
  alerts = 'alerts',
  dashboard = 'dashboard',
  analytics = 'analytics',
  userSupport = 'userSupport',
  invoice = 'invoice',
}

const menu: NavbarMenuItemData[] = [
  {
    id: MenuPages.orders,
    moduleKey: 'orders',
    to: `${v2Path}/${OrdersTabRoutes.orders}`,
    icon: <Orders />,
    roles: Object.values(UserRole),
    langKey: 'general.orders',
    groups: [NavbarMenuGroup.shipping],
  },
  {
    id: MenuPages.tracking,
    moduleKey: 'tracking',
    to: `${v2Path}/${EnumSettingRoutes.tracking}`,
    icon: <Tracking />,
    roles: Object.values(UserRole),
    langKey: 'general.tracking',
    groups: [NavbarMenuGroup.postShipping],
  },
  {
    id: MenuPages.returns,
    moduleKey: 'returns',
    to: `${v2Path}/${ReturnsTabRoutes.returns}`,
    icon: <ExchangeV2 />,
    roles: Object.values(UserRole),
    langKey: 'general.returns',
    groups: [NavbarMenuGroup.postShipping],
    divider: true,
  },
  {
    id: MenuPages.emailsSent,
    moduleKey: 'notifications',
    to: `${v2Path}/${GeneralRoutes.emails}/${GeneralRoutes.sent}`,
    icon: <Notifications />,
    roles: [UserRole.Owner, UserRole.Administrator, UserRole.Manager, UserRole.CustomerSupport],
    langKey: 'general.sentEmails',
    groups: [NavbarMenuGroup.postShipping],
  },
  {
    id: MenuPages.desk,
    moduleKey: 'desk',
    to: `${v2Path}/desk`,
    icon: <DeskIcon />,
    roles: [UserRole.Owner, UserRole.Administrator, UserRole.Manager, UserRole.CustomerSupport],
    langKey: 'general.outvioDesk',
    groups: [NavbarMenuGroup.postShipping],
  },
  {
    id: MenuPages.dashboard,
    moduleKey: 'dashboard',
    to: `${v2Path}/dashboard`,
    icon: <Dashboard />,
    roles: [UserRole.Owner, UserRole.Administrator, UserRole.Manager, UserRole.Warehouse],
    langKey: 'general.dashboard',
    groups: [NavbarMenuGroup.mobile],
  },
  {
    id: MenuPages.analytics,
    moduleKey: 'analytics',
    to: `${v2Path}/analytics`,
    icon: <Analytics />,
    roles: [UserRole.Owner, UserRole.Administrator, UserRole.Manager],
    langKey: 'general.analytics',
    groups: [NavbarMenuGroup.secondaryMenu],
  },
  {
    id: MenuPages.userSupport,
    to: `${v2Path}/user-support`,
    icon: <HelpFilled />,
    roles: Object.values(UserRole),
    langKey: 'general.supportCenter',
    groups: [NavbarMenuGroup.mobile],
  },
  {
    id: MenuPages.invoice,
    moduleKey: 'invoiceChecking',
    to: `${v2Path}/${InvoiceCheckingTabRoutes.invoice}`,
    icon: <InvoiceChecking width={24} height={24} />,
    roles: [UserRole.Owner, UserRole.Administrator, UserRole.Manager],
    langKey: 'dashboard.invoiceChecking',
    groups: [NavbarMenuGroup.secondaryMenu],
  },
];

export interface NavbarDataByUserResponse {
  getByGroup: (menuGroup: NavbarMenuGroup[]) => NavbarMenuItemData[];
  getById: (pageId: MenuPages) => NavbarMenuItemData | undefined;
}

export function getNavbarDataByUser({
  roles = [],
  accessModules,
  isCustomRole = false,
}: {
  roles?: UserRole[];
  isMobile?: boolean;
  // Effective app-module keys for the user. When provided, tabs with a
  // `moduleKey` not in this list are hidden. When omitted (e.g. legacy callers),
  // module-access filtering is skipped and only role filtering applies.
  accessModules?: string[];
  // Custom-role users carry no system staff role, so they are gated purely by
  // their access modules (not by the legacy per-item `roles` list).
  isCustomRole?: boolean;
}): NavbarDataByUserResponse {
  // System-role users with no roles see nothing. Custom-role users legitimately
  // have an empty system-role list, so don't short-circuit them here.
  if (!isCustomRole && !roles.length) {
    return {
      getByGroup: () => [],
      getById: () => undefined,
    };
  }

  const _menu = menu.filter((item) => {
    if (isCustomRole) {
      // Gate by access module only. Items without a moduleKey (e.g. history,
      // support) are non-gated and always shown.
      if (!item.moduleKey) return true;
      return (accessModules ?? []).includes(item.moduleKey);
    }

    const hasRole = item.roles.some((role) => roles.includes(role));
    if (!hasRole) return false;

    // Items without a moduleKey are never gated by access.
    if (!item.moduleKey || !accessModules) return true;

    return accessModules.includes(item.moduleKey);
  });

  return {
    getByGroup: (menuGroup: NavbarMenuGroup[]) => {
      return _menu.filter((item) => item.groups.some((g) => menuGroup.includes(g)));
    },
    getById: (id) => {
      return _menu.find((item) => item.id === id);
    },
  };
}
